import { MenuItem, TextField, Typography } from '@mui/material';
import {
  AlignBottomSimple,
  AlignCenterHorizontalSimple,
  AlignCenterVerticalSimple,
  AlignLeftSimple,
  AlignRightSimple,
  AlignTopSimple,
  TextB,
  TextItalic,
  TextUnderline,
} from '@phosphor-icons/react';
import { Control, Controller } from 'react-hook-form';
import { ITypeNode } from '../../Context/FormBuilderContext';
import ColorField from './ColorField';

interface CustomizationFormProps {
  control: Control<any>;
  type: ITypeNode;
}

const fontFamily = [
  'Roboto, sans-serif',
  'Inter, sans-serif',
  'Arial, sans-serif',
  'Arial Black, sans-serif',
  'Comic Sans MS, cursive',
  'Courier New, monospace',
  'Georgia, serif',
  'Impact, sans-serif',
  'Lucida Console, monospace',
  'Lucida Sans Unicode, sans-serif',
  'Palatino Linotype, serif',
  'Tahoma, sans-serif',
  'Times New Roman, serif',
  'Trebuchet MS, sans-serif',
  'Verdana, sans-serif',
  'Garamond, serif',
  'Brush Script MT, cursive',
];

export function CustomizationForm({ control, type }: CustomizationFormProps) {
  return (
    <>
      {/* VERTICAL ALIGNMENT */}
      <div
        id="text-settings-align-vertical"
        style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
      >
        {type !== ITypeNode.IMAGE && type !== ITypeNode.OUTPUT && (
          <>
            <Typography
              style={{
                fontSize: '12px',
                fontWeight: '400',
                color: '#475467',
                fontStyle: 'normal',
              }}
            >
              Vertical Alignment
            </Typography>

            <Controller
              name="verticalAlign"
              control={control}
              render={({ field }) => (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <div
                    onClick={() => field.onChange('start')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      backgroundColor:
                        field.value === 'start' ? '#7388F7' : 'transparent',
                      color: field.value === 'start' ? 'white' : '#98A2B3',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <AlignTopSimple size={16} weight="bold" />
                  </div>

                  <div
                    onClick={() => field.onChange('center')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      backgroundColor:
                        field.value === 'center' ? '#7388F7' : 'transparent',
                      color: field.value === 'center' ? 'white' : '#98A2B3',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <AlignCenterVerticalSimple size={16} weight="bold" />
                  </div>

                  <div
                    onClick={() => field.onChange('end')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      backgroundColor:
                        field.value === 'end' ? '#7388F7' : 'transparent',
                      color: field.value === 'end' ? 'white' : '#98A2B3',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <AlignBottomSimple size={16} weight="bold" />
                  </div>
                </div>
              )}
            />
          </>
        )}

        <Controller
          name="backgroundColor"
          control={control}
          render={({ field }) => (
            <ColorField
              label="Background Color"
              value={field.value}
              onChange={field.onChange}
              placeholder="#ffffff"
            />
          )}
        />
      </div>

      {/*  PADDING */}
      <div
        id="padding-settings"
        style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
      >
        <Typography
          style={{
            fontSize: '12px',
            fontWeight: '400',
            color: '#475467',
            fontStyle: 'normal',
          }}
        >
          Node padding
        </Typography>

        <Controller
          name="padding"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              select
              sx={{
                width: '100%',
                '& .MuiInputBase-root': {
                  height: '32px',
                  borderRadius: '4px',
                  border: '1px solid #EAECF0',
                  backgroundColor: '#F9FAFB',
                  color: '#475467',
                  margin: '0px',
                },
              }}
              variant="outlined"
              size="small"
            >
              {[
                0, 4, 8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 64, 72,
              ].map(size => (
                <MenuItem key={size} value={size}>
                  {size}px
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </div>

      {/* FONT */}
      {type !== ITypeNode.IMAGE && (
        <>
          <div
            style={{ width: '100%', height: '1px', backgroundColor: '#EAECF0' }}
          />

          <div
            id="font-settings"
            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
          >
            <Typography
              style={{
                fontSize: '12px',
                fontWeight: '400',
                color: '#475467',
                fontStyle: 'normal',
              }}
            >
              Font
            </Typography>

            <div style={{ display: 'flex', gap: '8px' }}>
              <Controller
                name="fontFamily"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    sx={{
                      width: '60%',
                      '& .MuiInputBase-root': {
                        height: '32px',
                        borderRadius: '4px',
                        border: '1px solid #EAECF0',
                        backgroundColor: '#F9FAFB',
                        color: '#475467',
                        margin: '0px',
                        fontSize: '14px',
                      },
                    }}
                    variant="outlined"
                    size="small"
                  >
                    {fontFamily.map((font: string) => (
                      <MenuItem
                        key={font}
                        value={font}
                        style={{ fontFamily: font }}
                      >
                        {font.split(',')[0]}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <Controller
                name="fontSize"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    sx={{
                      width: '40%',
                      '& .MuiInputBase-root': {
                        height: '32px',
                        borderRadius: '4px',
                        border: '1px solid #EAECF0',
                        backgroundColor: '#F9FAFB',
                        color: '#475467',
                        margin: '0px',
                        fontSize: '14px',
                      },
                    }}
                    variant="outlined"
                    size="small"
                    onChange={e => field.onChange(Number(e.target.value))}
                  >
                    {[
                      8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 64, 72,
                    ].map(size => (
                      <MenuItem key={size} value={size}>
                        {size}px
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </div>

            {type !== ITypeNode.SUBMIT && type !== ITypeNode.SHORT_TEXT && (
              <Controller
                name="titleColor"
                control={control}
                render={({ field }) => (
                  <ColorField
                    label="Title Color"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="#000000"
                  />
                )}
              />
            )}

            {type === ITypeNode.SHORT_TEXT && (
              <Controller
                name="shortTextColor"
                control={control}
                render={({ field }) => (
                  <ColorField
                    label="Short Text Color"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="#000000"
                  />
                )}
              />
            )}

            {type === ITypeNode.SUBMIT && (
              <>
                <Controller
                  name="buttonColor"
                  control={control}
                  render={({ field }) => (
                    <ColorField
                      label="Button color"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="#ffffff"
                    />
                  )}
                />
                <Controller
                  name="buttonBackgroundColor"
                  control={control}
                  render={({ field }) => (
                    <ColorField
                      label="Button Background Color"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="#ffffff"
                    />
                  )}
                />
              </>
            )}
          </div>
        </>
      )}

      {/* FONT STYLES */}
      {type !== ITypeNode.IMAGE && (
        <div
          id="text-settings-styles"
          style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
        >
          <Typography
            style={{
              fontSize: '12px',
              fontWeight: '400',
              color: '#475467',
              fontStyle: 'normal',
            }}
          >
            Styles
          </Typography>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Controller
              name="isBold"
              control={control}
              render={({ field }) => (
                <div
                  onClick={() => field.onChange(!field.value)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    cursor: 'pointer',
                    backgroundColor: field.value ? '#7388F7' : 'transparent',
                    color: field.value ? 'white' : '#98A2B3',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <TextB size={16} />
                </div>
              )}
            />

            <Controller
              name="isItalic"
              control={control}
              render={({ field }) => (
                <div
                  onClick={() => field.onChange(!field.value)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    cursor: 'pointer',
                    backgroundColor: field.value ? '#7388F7' : 'transparent',
                    color: field.value ? 'white' : '#98A2B3',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <TextItalic size={16} />
                </div>
              )}
            />

            <Controller
              name="isUnderlined"
              control={control}
              render={({ field }) => (
                <div
                  onClick={() => field.onChange(!field.value)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    cursor: 'pointer',
                    backgroundColor: field.value ? '#7388F7' : 'transparent',
                    color: field.value ? 'white' : '#98A2B3',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <TextUnderline size={16} />
                </div>
              )}
            />
          </div>
        </div>
      )}

      {/* TEXT ALIGN */}
      {type !== ITypeNode.IMAGE && (
        <div
          id="text-settings-align"
          style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
        >
          <Typography
            style={{
              fontSize: '12px',
              fontWeight: '400',
              color: '#475467',
              fontStyle: 'normal',
            }}
          >
            Text alignment
          </Typography>

          <Controller
            name="textAlign"
            control={control}
            render={({ field }) => (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <div
                  onClick={() => field.onChange('left')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    cursor: 'pointer',
                    backgroundColor:
                      field.value === 'left' ? '#7388F7' : 'transparent',
                    color: field.value === 'left' ? 'white' : '#98A2B3',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <AlignLeftSimple size={16} weight="bold" />
                </div>

                <div
                  onClick={() => field.onChange('center')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    cursor: 'pointer',
                    backgroundColor:
                      field.value === 'center' ? '#7388F7' : 'transparent',
                    color: field.value === 'center' ? 'white' : '#98A2B3',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <AlignCenterHorizontalSimple size={16} weight="bold" />
                </div>

                <div
                  onClick={() => field.onChange('right')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    cursor: 'pointer',
                    backgroundColor:
                      field.value === 'right' ? '#7388F7' : 'transparent',
                    color: field.value === 'right' ? 'white' : '#98A2B3',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <AlignRightSimple size={16} weight="bold" />
                </div>
              </div>
            )}
          />
        </div>
      )}

      {/* QUESTION */}
      {type === ITypeNode.LIST && (
        <>
          <div
            style={{ width: '100%', height: '1px', backgroundColor: '#EAECF0' }}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography
              style={{
                fontSize: '12px',
                fontWeight: '400',
                color: '#475467',
                fontStyle: 'normal',
              }}
            >
              Font question
            </Typography>

            <div style={{ display: 'flex', gap: '8px' }}>
              <Controller
                name="questionFontFamily"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    variant="outlined"
                    size="small"
                    sx={{
                      width: '60%',
                      '& .MuiInputBase-root': {
                        height: '32px',
                        borderRadius: '4px',
                        border: '1px solid #EAECF0',
                        backgroundColor: '#F9FAFB',
                        color: '#475467',
                        margin: '0px',
                      },
                    }}
                  >
                    {fontFamily.map((font: string) => (
                      <MenuItem
                        key={font}
                        value={font}
                        style={{ fontFamily: font }}
                      >
                        {font.split(',')[0]}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <Controller
                name="questionFontSize"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    variant="outlined"
                    size="small"
                    onChange={e => field.onChange(Number(e.target.value))}
                    sx={{
                      width: '40%',
                      '& .MuiInputBase-root': {
                        height: '32px',
                        borderRadius: '4px',
                        border: '1px solid #EAECF0',
                        backgroundColor: '#F9FAFB',
                        color: '#475467',
                        margin: '0px',
                      },
                    }}
                  >
                    {[
                      8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 64, 72,
                    ].map(size => (
                      <MenuItem key={size} value={size}>
                        {size}px
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography
              style={{
                fontSize: '12px',
                fontWeight: '400',
                color: '#475467',
                fontStyle: 'normal',
              }}
            >
              Style question
            </Typography>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Controller
                name="isQuestionBold"
                control={control}
                render={({ field }) => (
                  <div
                    onClick={() => field.onChange(!field.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      backgroundColor: field.value ? '#7388F7' : 'transparent',
                      color: field.value ? 'white' : '#98A2B3',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <TextB size={18} />
                  </div>
                )}
              />

              <Controller
                name="isQuestionItalic"
                control={control}
                render={({ field }) => (
                  <div
                    onClick={() => field.onChange(!field.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      backgroundColor: field.value ? '#7388F7' : 'transparent',
                      color: field.value ? 'white' : '#98A2B3',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <TextItalic size={18} />
                  </div>
                )}
              />

              <Controller
                name="isQuestionUnderlined"
                control={control}
                render={({ field }) => (
                  <div
                    onClick={() => field.onChange(!field.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      backgroundColor: field.value ? '#7388F7' : 'transparent',
                      color: field.value ? 'white' : '#98A2B3',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <TextUnderline size={18} />
                  </div>
                )}
              />
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography
              style={{
                fontSize: '12px',
                fontWeight: '400',
                color: '#475467',
                fontStyle: 'normal',
              }}
            >
              Text align question
            </Typography>

            <Controller
              name="questionTextAlign"
              control={control}
              render={({ field }) => (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <div
                    onClick={() => field.onChange('left')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      backgroundColor:
                        field.value === 'left' ? '#7388F7' : 'transparent',
                      color: field.value === 'left' ? 'white' : '#98A2B3',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <AlignLeftSimple size={18} />
                  </div>

                  <div
                    onClick={() => field.onChange('center')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      backgroundColor:
                        field.value === 'center' ? '#7388F7' : 'transparent',
                      color: field.value === 'center' ? 'white' : '#98A2B3',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <AlignCenterHorizontalSimple size={18} />
                  </div>

                  <div
                    onClick={() => field.onChange('right')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      backgroundColor:
                        field.value === 'right' ? '#7388F7' : 'transparent',
                      color: field.value === 'right' ? 'white' : '#98A2B3',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <AlignRightSimple size={18} />
                  </div>
                </div>
              )}
            />
          </div>

          {type === ITypeNode.LIST && (
            <Controller
              name="questionColor"
              control={control}
              render={({ field }) => (
                <ColorField
                  label="Question Color"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="#000000"
                />
              )}
            />
          )}
        </>
      )}

      {/* SUBTITLE */}
      {(type === ITypeNode.INPUT || type === ITypeNode.OUTPUT) && (
        <>
          <div
            style={{ width: '100%', height: '1px', backgroundColor: '#EAECF0' }}
          />

          <div
            id="font-settings"
            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
          >
            <Typography
              style={{
                fontSize: '12px',
                fontWeight: '400',
                color: '#475467',
                fontStyle: 'normal',
              }}
            >
              Font subtitle
            </Typography>

            <div style={{ display: 'flex', gap: '8px' }}>
              <Controller
                name="subtitleFontFamily"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    {...field}
                    select
                    sx={{
                      width: '60%',
                      '& .MuiInputBase-root': {
                        height: '32px',
                        borderRadius: '4px',
                        border: '1px solid #EAECF0',
                        backgroundColor: '#F9FAFB',
                        color: '#475467',
                        margin: '0px',
                      },
                    }}
                    variant="outlined"
                    size="small"
                  >
                    {fontFamily.map((font: string) => (
                      <MenuItem
                        key={font}
                        value={font}
                        style={{ fontFamily: font }}
                      >
                        {font.split(',')[0]}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <Controller
                name="subtitleFontSize"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    sx={{
                      width: '40%',
                      '& .MuiInputBase-root': {
                        height: '32px',
                        borderRadius: '4px',
                        border: '1px solid #EAECF0',
                        backgroundColor: '#F9FAFB',
                        color: '#475467',
                        margin: '0px',
                      },
                    }}
                    fullWidth
                    variant="outlined"
                    size="small"
                    onChange={e => field.onChange(Number(e.target.value))}
                  >
                    {[
                      8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 64, 72,
                    ].map(size => (
                      <MenuItem key={size} value={size}>
                        {size}px
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </div>

            <Controller
              name="subtitleColor"
              control={control}
              render={({ field }) => (
                <ColorField
                  label="Subtitle Color"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="#000000"
                />
              )}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography
              style={{
                fontSize: '12px',
                fontWeight: '400',
                color: '#475467',
                fontStyle: 'normal',
              }}
            >
              Style subtitle
            </Typography>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Controller
                name="isSubtitleBold"
                control={control}
                render={({ field }) => (
                  <div
                    onClick={() => field.onChange(!field.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      backgroundColor: field.value ? '#7388F7' : 'transparent',
                      color: field.value ? 'white' : '#98A2B3',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <TextB size={16} />
                  </div>
                )}
              />

              <Controller
                name="isSubtitleItalic"
                control={control}
                render={({ field }) => (
                  <div
                    onClick={() => field.onChange(!field.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      backgroundColor: field.value ? '#7388F7' : 'transparent',
                      color: field.value ? 'white' : '#98A2B3',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <TextItalic size={18} />
                  </div>
                )}
              />

              <Controller
                name="isSubtitleUnderlined"
                control={control}
                render={({ field }) => (
                  <div
                    onClick={() => field.onChange(!field.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      backgroundColor: field.value ? '#7388F7' : 'transparent',
                      color: field.value ? 'white' : '#98A2B3',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <TextUnderline size={18} />
                  </div>
                )}
              />
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography
              style={{
                fontSize: '12px',
                fontWeight: '400',
                color: '#475467',
                fontStyle: 'normal',
              }}
            >
              Text align subtitle
            </Typography>

            <Controller
              name="subtitleTextAlign"
              control={control}
              render={({ field }) => (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <div
                    onClick={() => field.onChange('left')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      backgroundColor:
                        field.value === 'left' ? '#7388F7' : 'transparent',
                      color: field.value === 'left' ? 'white' : '#98A2B3',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <AlignLeftSimple size={18} />
                  </div>

                  <div
                    onClick={() => field.onChange('center')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      backgroundColor:
                        field.value === 'center' ? '#7388F7' : 'transparent',
                      color: field.value === 'center' ? 'white' : '#98A2B3',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <AlignLeftSimple size={18} />
                  </div>

                  <div
                    onClick={() => field.onChange('right')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '32px',
                      height: '32px',
                      cursor: 'pointer',
                      backgroundColor:
                        field.value === 'right' ? '#7388F7' : 'transparent',
                      color: field.value === 'right' ? 'white' : '#98A2B3',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <AlignRightSimple size={18} />
                  </div>
                </div>
              )}
            />
          </div>
        </>
      )}

      {type !== ITypeNode.IMAGE && (
        <div
          style={{ width: '100%', height: '1px', backgroundColor: '#EAECF0' }}
        />
      )}

      {/* BORDER RADIUS */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          marginBottom: '16px',
        }}
      >
        <Typography
          style={{
            fontSize: '12px',
            fontWeight: '400',
            color: '#475467',
            fontStyle: 'normal',
          }}
        >
          Border settings
        </Typography>

        <div style={{ display: 'flex', gap: '8px' }}>
          <Controller
            name="borderRadius"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                sx={{
                  width: '50%',
                  '& .MuiInputBase-root': {
                    height: '32px',
                    borderRadius: '4px',
                    border: '1px solid #EAECF0',
                    backgroundColor: '#F9FAFB',
                    color: '#475467',
                    margin: '0px',
                  },
                }}
                variant="outlined"
                size="small"
              >
                {[4, 8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 64, 72].map(
                  size => (
                    <MenuItem key={size} value={size}>
                      {size}px
                    </MenuItem>
                  ),
                )}
              </TextField>
            )}
          />

          <Controller
            name="borderWeight"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                sx={{
                  width: '50%',
                  '& .MuiInputBase-root': {
                    height: '32px',
                    borderRadius: '4px',
                    border: '1px solid #EAECF0',
                    backgroundColor: '#F9FAFB',
                    color: '#475467',
                    margin: '0px',
                  },
                }}
                variant="outlined"
                size="small"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(size => (
                  <MenuItem key={size} value={size}>
                    {size}px
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </div>

        <Controller
          name="borderColor"
          control={control}
          render={({ field }) => (
            <ColorField
              label="Border Color"
              value={field.value}
              onChange={field.onChange}
              placeholder="#cccccc"
            />
          )}
        />
      </div>
    </>
  );
}
